'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import VideoDropZone from './VideoDropZone';
import ThumbnailPicker from './ThumbnailPicker';
import TagInput from './TagInput';
import LicenseSelector from './LicenseSelector';
import PermissionToggles from './PermissionToggles';
import UploadStickyBar from './UploadStickyBar';
import { AlignLeft, Film, FolderOpen, Globe, Zap, Info, Monitor } from 'lucide-react';
import { saveClips, generateClipId } from '@/lib/clipStore';
import { useAuth } from '@/lib/AuthContext';
import { createClient } from '@/lib/supabase/client';
import type { ClipData } from '@/components/ClipCard';
import CategoryIcon from '@/components/ui/CategoryIcon';

interface UploadFormValues {
  title: string;
  description: string;
  projectName: string;
  language: string;
  resolution: string;
  fps: string;
  licenseType: string;
  downloadAllowed: boolean;
  remixAllowed: boolean;
  creditsRequired: boolean;
}

type UploadType = 'scenepack' | 'bundle';

const categories = [
  { key: 'cat-action', value: 'action', label: 'Action' },
  { key: 'cat-anime', value: 'anime', label: 'Anime' },
  { key: 'cat-movies', value: 'movies', label: 'Movies' },
  { key: 'cat-tvshows', value: 'tvshows', label: 'TV Shows' },
  { key: 'cat-gaming', value: 'gaming', label: 'Gaming' },
  { key: 'cat-celebrities', value: 'celebrities', label: 'Celebrities' },
  { key: 'cat-trailers', value: 'trailers', label: 'Trailers' },
  { key: 'cat-horror', value: 'horror', label: 'Horror' },
  { key: 'cat-scifi', value: 'scifi', label: 'Sci-Fi' },
  { key: 'cat-comedy', value: 'comedy', label: 'Comedy' },
  { key: 'cat-drama', value: 'drama', label: 'Drama' },
  { key: 'cat-documentary', value: 'documentary', label: 'Documentary' },
  { key: 'cat-kpop', value: 'kpop', label: 'K-Pop' },
  { key: 'cat-edits', value: 'edits', label: 'Edits' },
  { key: 'cat-cars', value: 'cars', label: 'Cars' },
  { key: 'cat-nature', value: 'nature', label: 'Nature' },
  { key: 'cat-sports', value: 'sports', label: 'Sports' },
  { key: 'cat-music', value: 'music', label: 'Music Videos' },
  { key: 'cat-memes', value: 'memes', label: 'Memes' },
  { key: 'cat-cinematic', value: 'cinematic', label: 'Cinematic' },
  { key: 'cat-vfx', value: 'vfx', label: 'VFX' },
];

const languages = [
  { key: 'lang-none', value: 'none', label: 'No dialogue / Music only' },
  { key: 'lang-en', value: 'en', label: 'English' },
  { key: 'lang-de', value: 'de', label: 'German' },
  { key: 'lang-ja', value: 'ja', label: 'Japanese' },
  { key: 'lang-es', value: 'es', label: 'Spanish' },
  { key: 'lang-fr', value: 'fr', label: 'French' },
  { key: 'lang-ko', value: 'ko', label: 'Korean' },
  { key: 'lang-zh', value: 'zh', label: 'Chinese' },
  { key: 'lang-pt', value: 'pt', label: 'Portuguese' },
  { key: 'lang-other', value: 'other', label: 'Other' },
];

const resolutions = [
  { key: 'res-8k', value: '8K', label: '8K (7680×4320)' },
  { key: 'res-4k', value: '4K', label: '4K (3840×2160)' },
  { key: 'res-2k', value: '2K', label: '2K / 1440p' },
  { key: 'res-1080', value: '1080p', label: '1080p Full HD' },
  { key: 'res-720', value: '720p', label: '720p HD' },
  { key: 'res-vertical', value: 'Vertical 9:16', label: 'Vertical 9:16 (Reels/TikTok)' },
];

const fpsOptions = [
  { key: 'fps-24', value: '24', label: '24 fps — Cinematic' },
  { key: 'fps-25', value: '25', label: '25 fps — European TV' },
  { key: 'fps-30', value: '30', label: '30 fps — Standard' },
  { key: 'fps-60', value: '60', label: '60 fps — Smooth' },
  { key: 'fps-120', value: '120', label: '120 fps — Slow Motion' },
  { key: 'fps-240', value: '240', label: '240 fps — Ultra Slow Motion' },
];

export default function UploadContent() {
  const [uploadType, setUploadType] = useState<UploadType>('scenepack');
  const [tags, setTags] = useState<string[]>([]);
  const [license, setLicense] = useState('CC0');
  const [permissions, setPermissions] = useState({
    downloadAllowed: true,
    remixAllowed: false,
    creditsRequired: false,
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bundleFiles, setBundleFiles] = useState<File[]>([]);
  const [scenepackFile, setScenepackFile] = useState<File | null>(null);
  const [thumbnailFile, setThumbnailFile] = useState<File | null>(null);
  const [isDirty, setIsDirty] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);

  const { user } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<UploadFormValues>({
    defaultValues: {
      language: 'none',
      resolution: '1080p',
      fps: '30',
    },
  });

  const toggleCategory = (value: string) => {
    setSelectedCategories((prev) =>
      prev.includes(value) ? prev.filter((c) => c !== value) : [...prev, value]
    );
    setIsDirty(true);
  };

  const handleTypeSwitch = (type: UploadType) => {
    setUploadType(type);
    setIsDirty(false);
    setBundleFiles([]);
    setScenepackFile(null);
  };

  const onSubmit = async (data: UploadFormValues) => {
    if (uploadType === 'scenepack' && !scenepackFile) {
            toast.error('Please upload a video file for your Scene Pack.');
      return;
    }
    if (uploadType === 'bundle' && bundleFiles.length === 0) {
      toast.error('Please add at least one video file to your bundle.');
      return;
    }
    if (selectedCategories.length === 0) {
      toast.error('Please select at least one category.');
      return;
    }
    setIsSubmitting(true);
    await new Promise((r) => setTimeout(r, 2200));

    const uploaderName = user?.profile?.displayName || user?.profile?.username || 'Anonymous';
    const uploaderAvatar = user?.profile?.avatarUrl || '';
    const uploaderUsername = user?.profile?.username || '';
    const now = new Date();
    const uploadedAt = now.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

    const baseClip: Omit<ClipData, 'id'> = {
      title: data.title,
      uploader: uploaderName,
      uploaderAvatar,
      uploaderUsername,
      verified: false,
      premium: false,
      thumbnail: thumbnailFile ? URL.createObjectURL(thumbnailFile) : '',
      videoUrl: scenepackFile ? URL.createObjectURL(scenepackFile) : undefined,
      duration: '0:00',
      resolution: data.resolution,
      fps: parseInt(data.fps, 10),
      licenseType: license as ClipData['licenseType'],
      likes: 0,
      downloads: 0,
      views: 0,
      tags,
      uploadedAt,
      categories: selectedCategories,
    };

    const clips: ClipData[] =
      uploadType === 'scenepack'
        ? [{ ...baseClip, id: generateClipId() }]
        : bundleFiles.map((file) => ({
            ...baseClip,
            id: generateClipId(),
            videoUrl: URL.createObjectURL(file),
            title:
              bundleFiles.length > 1
                ? `${data.title} — ${file.name.replace(/\.[^.]+$/, '')}`
                : data.title,
          }));

    saveClips(clips);

    if (user?.id && uploaderUsername) {
      const supabase = createClient();
      const dbClips = clips.map((clip) => ({
        id: clip.id,
        user_id: user.id,
        title: clip.title,
        description: data.description || '',
        thumbnail_url: clip.thumbnail || '',
        video_url: '',
        category: selectedCategories[0] || '',
        tags: tags,
        duration: clip.duration || '0:00',
        file_size: '',
        resolution: data.resolution,
        fps: data.fps,
        license: license,
        allow_commercial: permissions.downloadAllowed,
        allow_edit: permissions.remixAllowed,
        require_credit: permissions.creditsRequired,
        is_public: true,
        creator_username: uploaderUsername.toLowerCase(),
        creator_display_name: uploaderName,
        creator_avatar_url: uploaderAvatar,
      }));

      const { error } = await supabase.from('clips').insert(dbClips);
      if (error) {
        console.log('Clip save to DB error:', error.message);
      }
    }

    if (uploadType === 'scenepack') {
      toast.success(`Scenepack "${data.title}" uploaded! Visible in: ${selectedCategories.join(', ')}.`);
    } else {
      toast.success(
        `Bundle of ${bundleFiles.length} clip${bundleFiles.length !== 1 ? 's' : ''} uploaded! Visible in: ${selectedCategories.join(', ')}.`
      );
    }

    setIsSubmitting(false);
    reset();
    setTags([]);
    setBundleFiles([]);
    setScenepackFile(null);
    setThumbnailFile(null);
    setSelectedCategories([]);
    setIsDirty(false);
  };

  const handleFormChange = () => setIsDirty(true);

  const formSectionClass = 'rounded-2xl p-6 mb-5';
  const sectionStyle = { background: 'var(--card)', border: '1px solid var(--border)' };

  const hasFile = uploadType === 'scenepack' ? !!scenepackFile : bundleFiles.length > 0;

  return (
    <form
      onSubmit={handleSubmit(onSubmit)}
      onChange={handleFormChange}
      className="max-w-screen-2xl mx-auto px-4 xl:px-8 2xl:px-12 pt-6"
    >
      {/* Page Header */}
      <div className="mb-6">
        <h1 className="font-extrabold text-2xl mb-1" style={{ color: 'var(--foreground)', letterSpacing: '-0.03em' }}>
          Upload
        </h1>
        <p className="text-sm" style={{ color: 'var(--muted-foreground)' }}>
          Share your content with 280,000+ editors worldwide.
        </p>
      </div>

      {/* Upload Type Selector */}
      <div className="mb-6">
        <div
          className="inline-flex rounded-2xl p-1 gap-1"
          style={{ background: 'var(--card)', border: '1px solid var(--border)' }}
        >
          <button
            type="button"
            onClick={() => handleTypeSwitch('scenepack')}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200"
            style={
              uploadType === 'scenepack'
                ? { background: 'var(--primary)', color: '#fff' }
                : { background: 'transparent', color: 'var(--muted-foreground)' }
            }
          >
            <CategoryIcon category="scenepacks" size={15} color={uploadType === 'scenepack' ? '#fff' : undefined} />
            Scenepack
          </button>
          <button
            type="button"
            onClick={() => handleTypeSwitch('bundle')}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-200"
            style={
              uploadType === 'bundle'
                ? { background: 'var(--primary)', color: '#fff' }
                : { background: 'transparent', color: 'var(--muted-foreground)' }
            }
          >
            <CategoryIcon category="clip bundle" size={15} color={uploadType === 'bundle' ? '#fff' : undefined} />
            Clip Bundle
          </button>
        </div>

        {/* Type description */}
        <div
          className="mt-3 flex items-center gap-2 p-3 rounded-xl text-xs"
          style={
            uploadType === 'scenepack'
              ? { background: 'rgba(108, 99, 255, 0.08)', border: '1px solid rgba(108, 99, 255, 0.2)', color: 'rgba(108, 99, 255, 0.9)' }
              : { background: 'rgba(59, 158, 255, 0.08)', border: '1px solid rgba(59, 158, 255, 0.2)', color: 'rgba(59, 158, 255, 0.9)' }
          }
        >
          {uploadType === 'scenepack' ? (
            <>
              <CategoryIcon category="scenepacks" size={13} color="rgba(108, 99, 255, 0.9)" />
              Scene Pack mode: upload <strong>exactly 1 video file</strong>. Adding a second file is not allowed — each scene pack contains one video.
            </>
          ) : (
            <>
              <CategoryIcon category="clip bundle" size={13} color="rgba(59, 158, 255, 0.9)" />
              Clip Bundle mode: upload <strong>multiple individual video clips</strong> or <strong>one ZIP archive</strong> containing all clips. Shared metadata applies to all.
            </>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 2xl:grid-cols-3 gap-6">
        {/* Left / Main Column */}
        <div className="xl:col-span-2 space-y-5">

          {/* File Upload Zone */}
          <div className={formSectionClass} style={sectionStyle}>
            {uploadType === 'scenepack' ? (
              <>
                <div className="flex items-center gap-2 mb-4">
                  <CategoryIcon category="scenepacks" size={17} />
                  <h2 className="font-bold text-base" style={{ color: 'var(--foreground)' }}>
                    Scene Pack File
                  </h2>
                  <span
                    className="text-xs font-medium px-2 py-0.5 rounded-md"
                    style={{ background: 'rgba(239, 68, 68, 0.12)', color: 'var(--danger)', border: '1px solid rgba(239,68,68,0.25)' }}
                  >
                    Required
                  </span>
                  {scenepackFile && (
                    <span
                      className="text-xs font-semibold px-2 py-0.5 rounded-md ml-auto"
                      style={{ background: 'rgba(34, 197, 94, 0.12)', color: 'var(--success)', border: '1px solid rgba(34,197,94,0.25)' }}
                    >
                      Ready
                    </span>
                  )}
                </div>
                <VideoDropZone
                  onFileSelect={setScenepackFile}
                  selectedFile={scenepackFile}
                  scenepackMode={true}
                />
                <p className="text-xs mt-3 flex items-center gap-1.5" style={{ color: 'var(--muted-foreground)' }}>
                  <Info size={12} />
                  Accepted formats: MP4, MOV, AVI, MKV, WebM · Max 4GB per file · 1 file only
                </p>
              </>
            ) : (
              <>
                <div className="flex items-center gap-2 mb-4">
                  <CategoryIcon category="clip bundle" size={17} />
                  <h2 className="font-bold text-base" style={{ color: 'var(--foreground)' }}>
                    Clip Bundle
                  </h2>
                  <span
                    className="text-xs font-medium px-2 py-0.5 rounded-md"
                    style={{ background: 'rgba(239, 68, 68, 0.12)', color: 'var(--danger)', border: '1px solid rgba(239,68,68,0.25)' }}
                  >
                    Required
                  </span>
                  {bundleFiles.length > 0 && (
                    <span
                      className="text-xs font-semibold px-2 py-0.5 rounded-md ml-auto"
                      style={{ background: 'rgba(34, 197, 94, 0.12)', color: 'var(--success)', border: '1px solid rgba(34,197,94,0.25)' }}
                    >
                      {bundleFiles.length} clip{bundleFiles.length !== 1 ? 's' : ''}
                    </span>
                  )}
                </div>
                <VideoDropZone
                  onFileSelect={() => {}}
                  selectedFile={null}
                  bundleMode={true}
                  onFilesSelect={setBundleFiles}
                  selectedFiles={bundleFiles}
                />
                <p className="text-xs mt-3 flex items-center gap-1.5" style={{ color: 'var(--muted-foreground)' }}>
                  <Info size={12} />
                  Upload multiple individual clips or one ZIP archive · Max 4GB per clip
                </p>
              </>
            )}
          </div>

          {/* Basic Info */}
          <div className={formSectionClass} style={sectionStyle}>
            <div className="flex items-center gap-2 mb-5">
              <AlignLeft size={17} style={{ color: 'var(--primary)' }} />
              <h2 className="font-bold text-base" style={{ color: 'var(--foreground)' }}>Basic Information</h2>
            </div>

            {/* Title */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--foreground)' }}>
                {uploadType === 'scenepack' ? 'Scenepack Title' : 'Bundle Title'}{' '}
                <span style={{ color: 'var(--danger)' }}>*</span>
              </label>
              <p className="text-xs mb-2" style={{ color: 'var(--muted-foreground)' }}>
                A clear title helps editors find your content. Include the scene, mood, or source.
              </p>
              <input
                type="text"
                {...register('title', {
                  required: 'Title is required',
                  minLength: { value: 5, message: 'At least 5 characters' },
                  maxLength: { value: 100, message: 'Max 100 characters' },
                })}
                placeholder={
                  uploadType === 'scenepack' ?'e.g. Attack on Titan Season 4 — Best Moments Scenepack' :'e.g. Cinematic Tokyo Rain — 4K Night Drive'
                }
                className="input-field w-full px-4 py-3 text-sm"
              />
              {errors.title && (
                <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>{errors.title.message}</p>
              )}
            </div>

            {/* Description */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--foreground)' }}>
                Description
              </label>
              <p className="text-xs mb-2" style={{ color: 'var(--muted-foreground)' }}>
                {uploadType === 'scenepack' ?'Describe the scenepack contents, source, clip count, and what editors can use it for.' : "Describe the clips' content, color grading, mood, and potential use cases."}
              </p>
              <textarea
                {...register('description', { maxLength: { value: 1000, message: 'Max 1000 characters' } })}
                placeholder={
                  uploadType === 'scenepack' ?'Contains 120+ clips from AoT S4. Includes action scenes, emotional moments, and transitions. Perfect for AMVs and edits...' :'Shot in Tokyo during a rainy night using a Sony A7S III. Color graded with warm tones. Perfect for music videos, travel edits, or cinematic intros...'
                }
                rows={4}
                className="input-field w-full px-4 py-3 text-sm resize-none"
              />
              {errors.description && (
                <p className="text-xs mt-1" style={{ color: 'var(--danger)' }}>{errors.description.message}</p>
              )}
            </div>

            {/* Tags */}
            <div>
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--foreground)' }}>
                Tags
              </label>
              <p className="text-xs mb-2" style={{ color: 'var(--muted-foreground)' }}>
                Add up to 10 tags. Press Enter or comma to add. Good tags: mood, style, subject, color.
              </p>
              <TagInput tags={tags} onTagsChange={setTags} maxTags={10} />
            </div>
          </div>

          {/* Source Info */}
          <div className={formSectionClass} style={sectionStyle}>
            <div className="flex items-center gap-2 mb-5">
              <Film size={17} style={{ color: 'var(--primary)' }} />
              <h2 className="font-bold text-base" style={{ color: 'var(--foreground)' }}>Source & Categories</h2>
            </div>

            {/* Project / Film Name */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--foreground)' }}>
                Film / Project Name
              </label>
              <p className="text-xs mb-2" style={{ color: 'var(--muted-foreground)' }}>
                Source film, show, or your own project name
              </p>
              <input
                type="text"
                {...register('projectName')}
                placeholder="e.g. Attack on Titan, Blade Runner 2049, Original Footage"
                className="input-field w-full px-4 py-3 text-sm"
              />
            </div>

            {/* Multi-select Categories */}
            <div>
              <div className="flex items-center gap-2 mb-1.5">
                <label className="block text-sm font-medium" style={{ color: 'var(--foreground)' }}>
                  Categories <span style={{ color: 'var(--danger)' }}>*</span>
                </label>
                {selectedCategories.length > 0 && (
                  <span
                    className="text-xs font-semibold px-2 py-0.5 rounded-md"
                    style={{ background: 'rgba(108, 99, 255, 0.12)', color: 'var(--primary)', border: '1px solid rgba(108,99,255,0.25)' }}
                  >
                    {selectedCategories.length} selected
                  </span>
                )}
              </div>
              <p className="text-xs mb-3" style={{ color: 'var(--muted-foreground)' }}>
                Select all categories where your content should appear.
              </p>
              <div className="flex flex-wrap gap-2">
                {categories.map((cat) => {
                  const isSelected = selectedCategories.includes(cat.value);
                  return (
                    <button
                      key={cat.key}
                      type="button"
                      onClick={() => toggleCategory(cat.value)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all duration-150"
                      style={
                        isSelected
                          ? { background: 'var(--primary)', color: '#fff', border: '1px solid var(--primary)' }
                          : { background: 'var(--muted)', color: 'var(--muted-foreground)', border: '1px solid var(--border)' }
                      }
                    >
                      <CategoryIcon
                        category={cat.value}
                        size={11}
                        color={isSelected ? '#fff' : undefined}
                      />
                      {cat.label}
                    </button>
                  );
                })}
              </div>
              {selectedCategories.length === 0 && (
                <p className="text-xs mt-2" style={{ color: 'var(--danger)' }}>Please select at least one category.</p>
              )}
            </div>

            {/* Language */}
            <div className="mt-4">
              <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--foreground)' }}>
                Dialogue Language
              </label>
              <p className="text-xs mb-2" style={{ color: 'var(--muted-foreground)' }}>
                Language spoken in the clips (if any)
              </p>
              <select
                {...register('language')}
                className="input-field w-full px-4 py-3 text-sm appearance-none"
              >
                {languages.map((lang) => (
                  <option key={lang.key} value={lang.value}>{lang.label}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Technical Specs */}
          <div className={formSectionClass} style={sectionStyle}>
            <div className="flex items-center gap-2 mb-5">
              <Monitor size={17} style={{ color: 'var(--primary)' }} />
              <h2 className="font-bold text-base" style={{ color: 'var(--foreground)' }}>Technical Specifications</h2>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Resolution */}
              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--foreground)' }}>
                  Resolution <span style={{ color: 'var(--danger)' }}>*</span>
                </label>
                <p className="text-xs mb-2" style={{ color: 'var(--muted-foreground)' }}>
                  Native resolution of the uploaded files
                </p>
                <select
                  {...register('resolution', { required: 'Resolution is required' })}
                  className="input-field w-full px-4 py-3 text-sm appearance-none font-mono"
                >
                  {resolutions.map((res) => (
                    <option key={res.key} value={res.value}>{res.label}</option>
                  ))}
                </select>
              </div>

              {/* FPS */}
              <div>
                <label className="block text-sm font-medium mb-1.5" style={{ color: 'var(--foreground)' }}>
                  Frame Rate (FPS) <span style={{ color: 'var(--danger)' }}>*</span>
                </label>
                <p className="text-xs mb-2" style={{ color: 'var(--muted-foreground)' }}>
                  Frames per second of the original recording
                </p>
                <select
                  {...register('fps', { required: 'FPS is required' })}
                  className="input-field w-full px-4 py-3 text-sm appearance-none font-mono"
                >
                  {fpsOptions.map((fps) => (
                    <option key={fps.key} value={fps.value}>{fps.label}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Specs info bar */}
            <div
              className="mt-4 flex items-center gap-2 p-3 rounded-xl text-xs"
              style={{ background: 'rgba(59, 158, 255, 0.08)', border: '1px solid rgba(59, 158, 255, 0.2)', color: 'rgba(59, 158, 255, 0.9)' }}
            >
              <Zap size={13} />
              4K and higher resolution clips are automatically featured in the Premium quality filter — they get 3× more downloads on average.
            </div>
          </div>

        </div>

        {/* Right / Sidebar Column */}
        <div className="space-y-5">

          {/* Thumbnail */}
          <div className={formSectionClass} style={sectionStyle}>
            <div className="flex items-center gap-2 mb-4">
              <FolderOpen size={17} style={{ color: 'var(--primary)' }} />
              <h2 className="font-bold text-base" style={{ color: 'var(--foreground)' }}>Thumbnail</h2>
            </div>
            <ThumbnailPicker onFileSelect={setThumbnailFile} selectedFile={thumbnailFile} />
            <p className="text-xs mt-3" style={{ color: 'var(--muted-foreground)' }}>
              Recommended: 1280×720px JPG or PNG. A good thumbnail increases clicks by up to 40%.
            </p>
          </div>

          {/* License */}
          <div className={formSectionClass} style={sectionStyle}>
            <div className="flex items-center gap-2 mb-4">
              <Globe size={17} style={{ color: 'var(--primary)' }} />
              <h2 className="font-bold text-base" style={{ color: 'var(--foreground)' }}>License Type</h2>
            </div>
            <LicenseSelector selected={license} onSelect={setLicense} />
          </div>

          {/* Permissions */}
          <div className={formSectionClass} style={sectionStyle}>
            <div className="flex items-center gap-2 mb-4">
              <Zap size={17} style={{ color: 'var(--primary)' }} />
              <h2 className="font-bold text-base" style={{ color: 'var(--foreground)' }}>Permissions</h2>
            </div>
            <PermissionToggles permissions={permissions} onPermissionsChange={setPermissions} />
          </div>

          {/* Upload Guidelines */}
          <div
            className="rounded-2xl p-4"
            style={{ background: 'rgba(108, 99, 255, 0.06)', border: '1px solid rgba(108, 99, 255, 0.2)' }}
          >
            <p className="text-xs font-semibold mb-2" style={{ color: 'var(--primary)' }}>
              📋 Upload Guidelines
            </p>
            <ul className="space-y-1.5">
              {[
                'Only upload clips you own or have rights to share',
                'No copyrighted music in the audio track',
                'No explicit content, violence, or hate speech',
                'Minimum 3 seconds, maximum 10 minutes per clip',
                'False license claims result in account suspension',
              ].map((guideline, i) => (
                <li
                  key={`guideline-${i + 1}`}
                  className="text-xs flex items-start gap-1.5"
                  style={{ color: 'rgba(240,240,248,0.65)' }}
                >
                  <span style={{ color: 'var(--primary)', flexShrink: 0 }}>·</span>
                  {guideline}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* Sticky Submit Bar */}
      <UploadStickyBar
        isSubmitting={isSubmitting}
        isDirty={isDirty}
        hasVideo={hasFile}
        onReset={() => {
          reset();
          setTags([]);
          setBundleFiles([]);
          setScenepackFile(null);
          setThumbnailFile(null);
          setSelectedCategories([]);
          setIsDirty(false);
        }}
        uploadMode={uploadType === 'scenepack' ? 'scenepack' : 'bundle'}
        bundleCount={bundleFiles.length}
      />
    </form>
  );
}